// 203-glance §C.2 [RULED — Laurent, 2026-09-21: "the firm's current fonts, same
// as used in the app, so the document is same style as the app entirely"] —
// THE FIRST STYLING THE EXCEL FILES EVER HAD: the header row's fill and ink,
// the app's faces (DM Sans for text, JetBrains Mono for figures), bold where
// the screen is bold. The column widths SheetJS wrote stay as written.
//
// WHY A POST-PROCESSOR AND NOT `cell.s`. SheetJS Community 0.20.3 — the `xlsx`
// this tree pins, on both planes — DROPS cell styles on write: its
// `get_cell_style` (node_modules/xlsx/xlsx.js:15695 at this commit) indexes the
// NUMBER FORMAT alone and writes `fontId="0" fillId="0"` for every cell, so a
// `cell.s` font or fill never reaches styles.xml. The Pro build is a licence,
// and a second SheetJS (xlsx-js-style, a 0.18.5 fork with that build's own
// advisories) would ship two copies of the parser. So the workbook SheetJS
// writes is taken AS BYTES and the styling APPENDED to it: the zip's
// `xl/styles.xml` and its one sheet are small, regular XML — MEASURED on the
// before-oracle (drive-203a2-costs.js on `db0f80b3992e`, the file unzipped):
// one font (Calibri 12, `scheme=minor`), two fills (none, gray125 — Excel's
// mandatory pair), one xf, and every cell written as `<c r="A1" t="str">` or
// `<c r="B3">`, never an `s=` — so fonts and a fill are added, xfs appended
// after the ones SheetJS wrote (a cell that already carried an xf keeps its
// number format inside the new one), and `s="n"` planted on the cells that
// take a style. Values, the sheet's shape, `<cols>` and every other part of
// the zip are untouched. The zip itself is opened and re-packed by SheetJS's
// OWN container code — `XLSX.CFB` (`fileType: "zip"`), the reader/writer the
// library packs every xlsx with, already in the main chunk — handed in as
// `cfbApi` so this module stays a pure function of bytes and never names a
// parser of its own (a JSZip import was measured first: docx ships its own
// compiled copy, so the app's would have been a SECOND one — 100 kB raw,
// 31 kB gzip, on a bundle ratchet already 6 % into its 10 % allowance).
//
// THE FALLBACK IS THE FAMILY: Excel names ONE face per font and substitutes
// itself when it is absent; `<family val="2"/>` (swiss) and `val="3"` (modern,
// fixed pitch) are what its substitution reads, so a reader without DM Sans
// gets a sans, one without JetBrains Mono a monospace — Laurent's "name and
// fallback, we'll see". The `<scheme val="minor"/>` SheetJS writes on its
// default font is DROPPED from the replacement: a scheme font resolves to the
// workbook THEME's minor face (Calibri) and would override the name.
//
// 203-glance-2 §C — THE TEXT FACE IS THE EXPORTING USER'S (tableExport.ts
// `facesOf`), and it may be NULL: a reader whose preference is the system
// default renders a face the browser can measure but not always name (macOS's
// system font). With null, font 0 stays EXACTLY as SheetJS wrote it — Calibri
// with its `scheme=minor`, which is to say the workbook's own default face —
// and the header and bold text fonts are built from that same element (its
// name, family and scheme kept), so the workbook wears Excel's default rather
// than a face the reader did not see. Nothing else moves.
//
// PURE: no DOM, no network, no import — a Node harness runs it on a file with
// the same `XLSX.CFB` the browser hands in (the drives do: the styled workbook
// re-read with SheetJS and its XML inspected).

/** @param {string} letters an A1 column reference's letters → its 0-based index */
const colIndex = (letters) => {
  let n = 0;
  for (const ch of letters) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
};
/** @param {string} c "#4a7cff" or "4a7cff" → "4A7CFF" */
const hex6 = (c) => String(c).replace(/^#/, "").toUpperCase();
/** @param {string} s */
const escXml = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/"/g, "&quot;");
/**
 * @param {string} xml one element's text
 * @param {string} name an attribute name
 * @returns {string | null}
 */
const attrOf = (xml, name) => {
  const m = new RegExp(`\\b${name}="([^"]*)"`).exec(xml);
  return m ? m[1] : null;
};

/**
 * @typedef {object} XlsxStyleSpec
 * @property {string | null} textFont the text face, named as the app names it ("DM Sans"); null keeps the workbook's own default face
 * @property {string} figureFont the figure face ("JetBrains Mono")
 * @property {string} headerFill the header row's fill, six-hex (the firm's colour)
 * @property {string} headerInk the header row's ink, six-hex (derived beside the fill)
 * @property {readonly number[]} rightCols the columns the screen right-aligns — figures: the figure face, right-aligned
 * @property {readonly number[]} [boldCols] columns the screen sets bold outside the header and total rows
 * @property {number | null} [totalRow] the totals row, 1-based, bold as on screen; null or absent when the sheet has none
 */

/**
 * @param {string} styles the workbook's xl/styles.xml
 * @param {XlsxStyleSpec} spec
 */
const buildStyles = (styles, spec) => {
  // ── fonts: font 0 becomes the text face (every unstyled cell reads it), four more appended ──
  const fontsM = /<fonts count="\d+">([\s\S]*?)<\/fonts>/.exec(styles);
  if (!fontsM) throw new Error("styles.xml: no <fonts>");
  const fonts = fontsM[1].match(/<font\b[^>]*\/>|<font\b[^>]*>[\s\S]*?<\/font>/g) || [];
  const szM = /<sz val="([^"]+)"\/>/.exec(fonts[0] || "");
  const sz = szM ? szM[1] : "12";
  const inkTheme = '<color theme="1"/>';
  const font = (name, family, color, bold) => `<font>${bold ? "<b/>" : ""}<sz val="${sz}"/>${color}<name val="${escXml(name)}"/><family val="${family}"/></font>`;
  // the default face as SheetJS wrote it — `<name …/>`, `<family …/>`, `<scheme …/>` — kept whole when no text face is named (203-glance-2 §C)
  const defaultFace = ((fonts[0] || "").match(/<name\b[^>]*\/>|<family\b[^>]*\/>|<scheme\b[^>]*\/>/g) || []).join("");
  const textFont = (color, bold) => (spec.textFont === null ? `<font>${bold ? "<b/>" : ""}<sz val="${sz}"/>${color}${defaultFace}</font>` : font(spec.textFont, 2, color, bold));
  if (spec.textFont !== null) fonts[0] = textFont(inkTheme, false);
  const fontId = (f) => fonts.push(f) - 1;
  const F_HEADER = fontId(textFont(`<color rgb="FF${hex6(spec.headerInk)}"/>`, true));
  const F_FIGURE = fontId(font(spec.figureFont, 3, inkTheme, false));
  const F_TEXT_BOLD = fontId(textFont(inkTheme, true));
  const F_FIGURE_BOLD = fontId(font(spec.figureFont, 3, inkTheme, true));
  // ── fills: the header's solid fill appended after Excel's mandatory pair ──
  const fillsM = /<fills count="\d+">([\s\S]*?)<\/fills>/.exec(styles);
  if (!fillsM) throw new Error("styles.xml: no <fills>");
  const fills = fillsM[1].match(/<fill\b[^>]*\/>|<fill\b[^>]*>[\s\S]*?<\/fill>/g) || [];
  const FILL_HEADER = fills.push(`<fill><patternFill patternType="solid"><fgColor rgb="FF${hex6(spec.headerFill)}"/><bgColor indexed="64"/></patternFill></fill>`) - 1;
  // ── cellXfs: the ones SheetJS wrote stay at their indices; new ones are appended and shared ──
  const xfsM = /<cellXfs count="\d+">([\s\S]*?)<\/cellXfs>/.exec(styles);
  if (!xfsM) throw new Error("styles.xml: no <cellXfs>");
  const xfs = xfsM[1].match(/<xf\b[^>]*\/>|<xf\b[^>]*>[\s\S]*?<\/xf>/g) || [];
  const numFmtOf = (xfIndex) => (xfIndex >= 0 && xfIndex < xfs.length ? attrOf(xfs[xfIndex], "numFmtId") || "0" : "0");
  /** @type {Map<string, number>} */
  const shared = new Map();
  const xfFor = (numFmt, fontIdx, fillIdx, right) => {
    const key = `${numFmt}|${fontIdx}|${fillIdx}|${right ? 1 : 0}`;
    const have = shared.get(key);
    if (have !== undefined) return have;
    const apply = `${numFmt !== "0" ? ' applyNumberFormat="1"' : ""} applyFont="1"${fillIdx !== 0 ? ' applyFill="1"' : ""}`;
    const xf = right
      ? `<xf numFmtId="${numFmt}" fontId="${fontIdx}" fillId="${fillIdx}" borderId="0" xfId="0"${apply} applyAlignment="1"><alignment horizontal="right"/></xf>`
      : `<xf numFmtId="${numFmt}" fontId="${fontIdx}" fillId="${fillIdx}" borderId="0" xfId="0"${apply}/>`;
    const idx = xfs.push(xf) - 1;
    shared.set(key, idx);
    return idx;
  };
  const rightCols = new Set(spec.rightCols);
  const boldCols = new Set(spec.boldCols || []);
  const totalRow = spec.totalRow ?? null;
  /**
   * the xf a cell takes, or null for none: the header row, the totals row, a figure column, a bold column
   * @param {number} row 1-based
   * @param {number} col 0-based
   * @param {number} currentXf the cell's own xf, -1 for none (its number format travels into the new one)
   */
  const styleOf = (row, col, currentXf) => {
    const right = rightCols.has(col);
    const numFmt = numFmtOf(currentXf);
    if (row === 1) return xfFor(numFmt, F_HEADER, FILL_HEADER, right);
    if (totalRow !== null && row === totalRow) return xfFor(numFmt, right ? F_FIGURE_BOLD : F_TEXT_BOLD, 0, right);
    const bold = boldCols.has(col);
    if (right) return xfFor(numFmt, bold ? F_FIGURE_BOLD : F_FIGURE, 0, true);
    if (bold) return xfFor(numFmt, F_TEXT_BOLD, 0, false);
    return null;
  };
  const finish = () =>
    styles
      .replace(fontsM[0], `<fonts count="${fonts.length}">${fonts.join("")}</fonts>`)
      .replace(fillsM[0], `<fills count="${fills.length}">${fills.join("")}</fills>`)
      .replace(xfsM[0], `<cellXfs count="${xfs.length}">${xfs.join("")}</cellXfs>`);
  return { styleOf, finish };
};

/**
 * @param {string} sheet one worksheet's XML
 * @param {(row: number, col: number, currentXf: number) => number | null} styleOf
 */
const styleSheet = (sheet, styleOf) =>
  sheet.replace(/<c r="([A-Z]+)(\d+)"([^>]*?)(\/?)>/g, (whole, letters, rowText, attrs, slash) => {
    const current = attrOf(attrs, "s");
    const xf = styleOf(Number(rowText), colIndex(letters), current === null ? -1 : Number(current));
    if (xf === null) return whole;
    const rest = attrs.replace(/\s+s="[^"]*"/, "");
    return `<c r="${letters}${rowText}" s="${xf}"${rest}${slash}>`;
  });

/**
 * @typedef {object} ZipEntry one file of the container, as SheetJS's CFB holds it
 * @property {string} name
 * @property {Uint8Array} content
 * @property {number} [size]
 */
/**
 * @typedef {object} Cfb the container: two parallel arrays, a path per entry
 * @property {string[]} FullPaths
 * @property {ZipEntry[]} FileIndex
 */
/**
 * @typedef {object} CfbApi SheetJS's `XLSX.CFB` — the container reader/writer the library itself packs every xlsx with
 * @property {(data: Uint8Array, opts: { type: "array" }) => Cfb} read
 * @property {(cfb: Cfb, path: string) => ZipEntry | null} find
 * @property {(cfb: Cfb, opts: { fileType: "zip"; type: "array"; compression: boolean }) => Uint8Array | ArrayBuffer} write
 */

/**
 * @param {ZipEntry} entry
 * @param {string} text the entry's new content
 */
const put = (entry, text) => {
  const b = new TextEncoder().encode(text);
  entry.content = b;
  entry.size = b.length;
};

/**
 * The workbook as SheetJS wrote it, dressed: returns the re-packed zip.
 * @param {ArrayBuffer | Uint8Array} bytes the workbook (`XLSX.write(wb, { bookType: "xlsx", type: "array" })`)
 * @param {XlsxStyleSpec} spec
 * @param {CfbApi} cfbApi `XLSX.CFB`
 * @returns {Uint8Array}
 */
export function styleXlsx(bytes, spec, cfbApi) {
  const cfb = cfbApi.read(bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes), { type: "array" });
  const stylesEntry = cfbApi.find(cfb, "/xl/styles.xml");
  if (!stylesEntry) throw new Error("not a SheetJS workbook: no xl/styles.xml");
  const dec = new TextDecoder();
  const { styleOf, finish } = buildStyles(dec.decode(stylesEntry.content), spec);
  cfb.FullPaths.forEach((p, i) => {
    if (!/\/xl\/worksheets\/sheet\d+\.xml$/.test(p)) return;
    const entry = cfb.FileIndex[i];
    put(entry, styleSheet(dec.decode(entry.content), styleOf));
  });
  put(stylesEntry, finish());
  const out = cfbApi.write(cfb, { fileType: "zip", type: "array", compression: true });
  return out instanceof Uint8Array ? out : new Uint8Array(out);
}
